import { Component } from '@theme/component';

/** Texto visible traducible (window.tmI18n, layout/theme.liquid); si falta, vale el español. */
function tr(key, fallback, vars) {
  const t = (window.tmI18n && window.tmI18n[key]) || fallback;
  return vars ? t.replace(/\{\{(\w+)\}\}/g, (_, n) => (vars[n] !== undefined ? vars[n] : '')) : t;
}
/** Decimales: coma en español, punto en el resto. */
const dec = (s) => ((document.documentElement.lang || 'es').startsWith('es') ? s.replace('.', ',') : s);


/**
 * Subida de la imagen del cliente para la página "Imprime tu mural".
 *
 * Qué hace, en orden:
 *  1. Valida el archivo en el navegador (tipo, peso y megapíxeles) con los
 *     mismos límites que el plan gratuito de Cloudinary, para dar un mensaje
 *     claro en vez del error crudo del servidor.
 *  2. Enseña la imagen al instante en la vista de pared (con una URL local),
 *     sin esperar a que termine la subida.
 *  3. La sube a Cloudinary con un preset "Unsigned" (no lleva ningún secreto:
 *     el nombre de la nube y el del preset son datos públicos).
 *  4. Publica el estado en el evento `mural:design`, que recoge la calculadora
 *     para decidir si deja añadir al carrito y qué propiedades manda en la
 *     línea del pedido (el enlace a la imagen, sobre todo).
 *
 * La calidad de impresión (puntos por pulgada) se recalcula cada vez que la
 * vista de pared publica unas medidas nuevas (`mural:dimensions`): con los
 * mismos píxeles, una pared más grande da menos densidad.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} zone
 * @property {HTMLInputElement} input
 * @property {HTMLElement} progress
 * @property {HTMLElement} progressBar
 * @property {HTMLElement} progressText
 * @property {HTMLElement} done
 * @property {HTMLElement} fileName
 * @property {HTMLElement} fileInfo
 * @property {HTMLElement} quality
 * @property {HTMLElement} error
 * @property {HTMLInputElement} rights
 *
 * @extends {Component<Refs>}
 */
class MuralUpload extends Component {
  requiredRefs = ['zone', 'input', 'progress', 'progressBar', 'progressText', 'done', 'fileName', 'fileInfo', 'quality', 'error', 'rights'];

  /** @type {{url: string, name: string, width: number, height: number, bytes: number} | null} */
  uploaded = null;
  /** Densidad en ppp con las últimas medidas, o null si aún no hay imagen. */
  ppi = null;
  uploading = false;
  /** @type {string | null} */
  blobUrl = null;
  /** @type {XMLHttpRequest | null} */
  request = null;

  connectedCallback() {
    super.connectedCallback();

    const { zone, input, rights } = this.refs;

    zone.addEventListener('click', () => input.click());
    zone.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        input.click();
      }
    });
    input.addEventListener('change', () => {
      const file = input.files && input.files[0];
      if (file) this.#handle(file);
      input.value = '';
    });

    for (const type of ['dragenter', 'dragover']) {
      zone.addEventListener(type, (event) => {
        event.preventDefault();
        zone.classList.add('is-over');
      });
    }
    for (const type of ['dragleave', 'drop']) {
      zone.addEventListener(type, (event) => {
        event.preventDefault();
        zone.classList.remove('is-over');
      });
    }
    zone.addEventListener('drop', (event) => {
      const files = /** @type {DragEvent} */ (event).dataTransfer?.files;
      if (!files || !files.length) return;
      if (files.length > 1) {
        this.#fail(tr('up_single', 'Sube una sola imagen: un único mural por pedido.'));
        return;
      }
      this.#handle(files[0]);
    });

    rights.addEventListener('change', () => this.#publish());

    this.onDimensions = (/** @type {Event} */ event) => {
      this.lastDimensions = /** @type {CustomEvent} */ (event).detail;
      this.#updateQuality();
    };
    document.addEventListener('mural:dimensions', this.onDimensions);

    this.#publish();
  }

  disconnectedCallback() {
    if (this.onDimensions) document.removeEventListener('mural:dimensions', this.onDimensions);
    if (this.blobUrl) URL.revokeObjectURL(this.blobUrl);
    super.disconnectedCallback?.();
  }

  /** Botón "Cambiar imagen" del resumen. */
  choose() {
    this.refs.input.click();
  }

  /** @param {File} file */
  async #handle(file) {
    const maxMb = Number(this.dataset.maxMb) || 10;
    const maxMegapixels = Number(this.dataset.maxMegapixels) || 25;
    const allowed = ['image/jpeg', 'image/png', 'image/webp'];

    this.#clearError();

    if (!allowed.includes(file.type)) {
      this.#fail(tr('up_type', 'Solo se admiten imágenes JPG, PNG o WebP.'));
      return;
    }
    if (file.size > maxMb * 1024 * 1024) {
      this.#fail(
        tr('up_size', 'La imagen pesa {{mb}} MB y el máximo es {{max}} MB. Si tu archivo es más grande, envíalo a {{email}} y lo preparamos nosotros.', { mb: dec((file.size / 1048576).toFixed(1)), max: maxMb, email: this.dataset.contactEmail })
      );
      return;
    }

    let size;
    try {
      size = await this.#measure(file);
    } catch {
      this.#fail(tr('up_unreadable', 'No hemos podido leer esa imagen. Prueba con otro archivo.'));
      return;
    }
    if (size.width * size.height > maxMegapixels * 1e6) {
      this.#fail(
        tr('up_mp', 'La imagen tiene {{mp}} megapíxeles y el máximo es {{max}}. Reduce su tamaño o envíala a {{email}}.', { mp: (size.width * size.height / 1e6).toFixed(0), max: maxMegapixels, email: this.dataset.contactEmail })
      );
      return;
    }

    // Se descarta la subida anterior, si la hay: solo vale una imagen por pedido.
    this.request?.abort();
    this.uploaded = null;
    if (this.blobUrl) URL.revokeObjectURL(this.blobUrl);
    this.blobUrl = URL.createObjectURL(file);

    const wall = /** @type {any} */ (document.querySelector('mural-wall-preview'));
    if (wall && typeof wall.setDesign === 'function') {
      wall.setDesign(this.blobUrl, size.width / size.height);
    }

    this.size = size;
    this.fileLabel = file.name;
    this.refs.fileName.textContent = file.name;
    this.refs.fileInfo.textContent = `${size.width} × ${size.height} px · ${dec((file.size / 1048576).toFixed(1))} MB`;
    this.#updateQuality();
    this.#upload(file, size);
  }

  /**
   * @param {File} file
   * @returns {Promise<{width: number, height: number}>}
   */
  #measure(file) {
    return new Promise((resolve, reject) => {
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => {
        URL.revokeObjectURL(url);
        resolve({ width: img.naturalWidth, height: img.naturalHeight });
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('no se pudo leer'));
      };
      img.src = url;
    });
  }

  /**
   * @param {File} file
   * @param {{width: number, height: number}} size
   */
  #upload(file, size) {
    const { progress, progressBar, progressText, done, zone } = this.refs;
    const { cloudName, uploadPreset } = this.dataset;
    if (!cloudName || !uploadPreset) {
      this.#fail(tr('up_not_configured', 'La subida de imágenes no está configurada todavía.'));
      return;
    }

    this.uploading = true;
    zone.hidden = true;
    done.hidden = true;
    progress.hidden = false;
    progressBar.style.width = '0%';
    progressText.textContent = tr('up_uploading', 'Subiendo tu imagen… {{p}} %', { p: 0 });
    this.#publish();

    const body = new FormData();
    body.append('upload_preset', uploadPreset);
    body.append('file', file);

    const xhr = new XMLHttpRequest();
    this.request = xhr;
    xhr.open('POST', `https://api.cloudinary.com/v1_1/${cloudName}/image/upload`);
    xhr.upload.addEventListener('progress', (event) => {
      if (!event.lengthComputable) return;
      const percent = Math.round((event.loaded / event.total) * 100);
      progressBar.style.width = `${percent}%`;
      progressText.textContent = tr('up_uploading', 'Subiendo tu imagen… {{p}} %', { p: percent });
    });
    xhr.addEventListener('load', () => {
      this.uploading = false;
      progress.hidden = true;
      let result = null;
      try {
        result = JSON.parse(xhr.responseText);
      } catch {
        /* se trata abajo */
      }
      if (xhr.status >= 200 && xhr.status < 300 && result && result.secure_url) {
        this.uploaded = {
          url: result.secure_url,
          name: this.fileLabel || file.name,
          width: result.width || size.width,
          height: result.height || size.height,
          bytes: result.bytes || file.size,
        };
        done.hidden = false;
        this.#publish();
      } else {
        const message = result && result.error && result.error.message ? ` (${result.error.message})` : '';
        this.#fail(tr('up_failed', 'No se ha podido subir la imagen{{m}}. Inténtalo de nuevo.', { m: message }), true);
      }
    });
    xhr.addEventListener('error', () => {
      this.uploading = false;
      progress.hidden = true;
      this.#fail(tr('up_connection', 'Se ha cortado la conexión al subir la imagen. Inténtalo de nuevo.'), true);
    });
    xhr.send(body);
  }

  /**
   * @param {string} message
   * @param {boolean} [keepPreview] - tras un fallo de red la imagen sigue en la vista de pared
   */
  #fail(message, keepPreview = false) {
    const { error, zone, done } = this.refs;
    this.uploading = false;
    this.uploaded = null;
    error.textContent = message;
    error.hidden = false;
    zone.hidden = false;
    done.hidden = true;
    if (!keepPreview) {
      this.ppi = null;
      this.refs.quality.hidden = true;
    }
    this.#publish();
  }

  #clearError() {
    this.refs.error.hidden = true;
    this.refs.error.textContent = '';
  }

  /** Puntos por pulgada reales con la pared y el encuadre actuales. */
  #updateQuality() {
    const { quality } = this.refs;
    const d = this.lastDimensions;
    if (!this.size || !d || !d.width) {
      quality.hidden = true;
      return;
    }
    const crop = d.crop;
    const fractionX = crop ? (crop.x2 - crop.x1) / 100 : 1;
    const pixelsUsed = this.size.width * fractionX;
    const inches = d.width / 2.54;
    const ppi = Math.round(pixelsUsed / inches);
    this.ppi = ppi;

    let level = 'ok';
    let text = tr('up_q_ok', 'Calidad de impresión buena ({{ppi}} ppp). Tu imagen se verá nítida.', { ppi });
    if (ppi < 25) {
      level = 'low';
      text = tr('up_q_low', 'Resolución baja ({{ppi}} ppp): a este tamaño se verá pixelada. Prueba con una imagen más grande o con una pared más pequeña.', { ppi });
    } else if (ppi < 50) {
      level = 'mid';
      text = tr('up_q_mid', 'Resolución justa ({{ppi}} ppp): se verá bien desde lejos y algo blanda de cerca.', { ppi });
    }
    quality.textContent = text;
    quality.dataset.level = level;
    quality.hidden = false;
    this.#publish();
  }

  /** Cuenta a la calculadora si ya se puede comprar y con qué imagen. */
  #publish() {
    let reason = '';
    if (this.uploading) reason = tr('up_wait', 'Espera a que termine de subirse tu imagen.');
    else if (!this.uploaded) reason = tr('up_need_image', 'Sube la imagen de tu mural para continuar.');
    else if (!this.refs.rights.checked) reason = tr('up_rights', 'Confirma que tienes derecho a imprimir esa imagen.');

    document.dispatchEvent(
      new CustomEvent('mural:design', {
        detail: {
          ready: !reason,
          reason,
          image: this.uploaded,
          ppi: this.ppi,
        },
      })
    );
  }
}

if (!customElements.get('mural-upload')) {
  customElements.define('mural-upload', MuralUpload);
}
